import { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { groupService, CreateGroupInput } from '../../../services/groupService';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateGroupModal = ({ isOpen, onClose, onSuccess }: CreateGroupModalProps) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [userCompanyName, setUserCompanyName] = useState<string | null>(null);
  const [userCompanyLogoUrl, setUserCompanyLogoUrl] = useState<string | null>(null);
  const [loadingCompanyProfile, setLoadingCompanyProfile] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [groupLimitInfo, setGroupLimitInfo] = useState<{
    current: number;
    max: number | null;
    remaining: number | null;
  } | null>(null);
  const [checkingLimit, setCheckingLimit] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkGroupCreationLimit();
      loadCompanyProfile();
    }
  }, [isOpen]);

  const loadCompanyProfile = async () => {
    if (!userProfile?.id) return;
    try {
      setLoadingCompanyProfile(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('company_name, company_logo_url')
        .eq('id', userProfile.id)
        .single();

      if (error) throw error;
      setUserCompanyName(data?.company_name || null);
      setUserCompanyLogoUrl(data?.company_logo_url || null);
    } catch (error: any) {
      console.error('Error loading company profile:', error);
    } finally {
      setLoadingCompanyProfile(false);
    }
  };

  const checkGroupCreationLimit = async () => {
    try {
      setCheckingLimit(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.rpc('can_create_group', {
        user_id_param: user.id
      });

      if (error) throw error;

      if (data) {
        setGroupLimitInfo({
          current: data.current_count || 0,
          max: data.max_allowed ?? null,
          remaining: data.remaining ?? null,
        });

        if (!data.allowed) {
          setErrors({ name: data.reason || 'Cannot create group' });
        } else {
          setErrors({}); // Clear errors if allowed
        }
      } else {
        // If no data returned, set default values
        setGroupLimitInfo({
          current: 0,
          max: null,
          remaining: null,
        });
      }
    } catch (error: any) {
      console.error('Failed to check group limit:', error);
      // Set default values on error to prevent null access
      setGroupLimitInfo({
        current: 0,
        max: null,
        remaining: null,
      });
    } finally {
      setCheckingLimit(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { name?: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSubmitting(true);
      
      // Create group with company profile from user profile
      const input: CreateGroupInput = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        companyName: userCompanyName || undefined,
        companyLogoUrl: userCompanyLogoUrl || undefined,
      };
      
      await groupService.createGroup(input);
      
      setFormData({ name: '', description: '' });
      setErrors({});
      onSuccess();
      onClose();
    } catch (error: any) {
      setErrors({ name: error.message || 'Failed to create group' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '' });
    setErrors({});
    setGroupLimitInfo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
              <Icon name="Plus" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Create New Group</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Group Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
            placeholder="Enter group name"
            disabled={submitting}
          />

          <Input
            label="Description (Optional)"
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter group description"
            disabled={submitting}
          />

          {/* Company Profile Info */}
          {loadingCompanyProfile ? (
            <div className="text-sm text-muted-foreground">Loading company profile...</div>
          ) : (userCompanyName || userCompanyLogoUrl) ? (
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <p className="text-sm font-medium text-foreground mb-2">Company Profile</p>
              <div className="flex items-center gap-3">
                {userCompanyLogoUrl && (
                  <img
                    src={userCompanyLogoUrl}
                    alt="Company logo"
                    className="w-12 h-12 rounded-lg object-cover border border-border"
                  />
                )}
                <div>
                  {userCompanyName && (
                    <p className="text-sm text-foreground">{userCompanyName}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    This will be used for the new group. Update your company profile in Settings to change it.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">
                No company profile set. You can add one in Settings → Company Profile.
              </p>
            </div>
          )}

          {checkingLimit ? (
            <div className="text-sm text-muted-foreground">Checking subscription limits...</div>
          ) : groupLimitInfo && (
            <div className="bg-muted/50 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Group Creation Limit</span>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Current groups: {groupLimitInfo.current}</p>
                {groupLimitInfo.max !== null ? (
                  <p>Maximum allowed: {groupLimitInfo.max}</p>
                ) : (
                  <p>Maximum allowed: Unlimited</p>
                )}
                {groupLimitInfo?.remaining !== null && groupLimitInfo?.remaining !== undefined && (
                  <p className="text-primary font-medium">
                    Remaining: {groupLimitInfo.remaining} group{groupLimitInfo.remaining !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="default"
              disabled={submitting || uploadingLogo || checkingLimit || (groupLimitInfo?.remaining !== null && groupLimitInfo?.remaining !== undefined && (groupLimitInfo.remaining ?? 0) <= 0)}
              className="flex-1"
            >
              {submitting || uploadingLogo ? 'Creating...' : 'Create Group'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;